<template>
  <div class="header">
    <div class="map">
      <div class="google-maps" id="map"></div>
    </div>

    <Drawer></Drawer>

    <Modal></Modal>
    <!-- Grade Information Popup -->
    <InformationPopup></InformationPopup>
  </div>
</template>

<script>
import * as electorates from "../../electorate.json";
import Drawer from "./Drawer.vue";
import Modal from "./ThreatenedSpeciesModal.vue";
import InformationPopup from "./InformationPopup.vue";

export default {
  name: "ThreatenedSpeciesMap",
  components: {
    Drawer,
    Modal,
    InformationPopup,
  },
  data() {
    return {
      gMap: null,
      MAP_ZOOM: 10,
      postcodeNotInAU: false,
      selectedElectorate: null,
      allElectorates: [],

      userPostcode: "",
      userPostcodeLat: "",
      userPostcodeLng: "",
    };
  },
  computed: {
    selectedAddress() {
      return this.$store.getters.selectedAddress;
    },

    electorateName() {
      return this.$store.getters.electorateName;
    },
  },
  watch: {
    async selectedAddress(newValue) {
      const postcodeCoordinates = this.$store.getters.addressCoordinates;
      if (!postcodeCoordinates) {
        console.error("No coordinates for this postcode");
        return;
      }

      const userPostcode = localStorage.getItem("postcode");
      const userPostcodeLat = localStorage.getItem("postcodeLng");
      const userPostcodeLng = localStorage.getItem("postcode");

      const isUserPostcode =
        !userPostcode || !userPostcodeLat || !userPostcodeLng;
      try {
        this.gMap.panTo(postcodeCoordinates);
        this.gMap.setZoom(this.MAP_ZOOM);

        if (isUserPostcode) {
          localStorage.setItem("postcodeLat", postcodeCoordinates.lat);
          localStorage.setItem("postcodeLng", postcodeCoordinates.lng);
          localStorage.setItem("inAU", "insideAU");

          const postcode = await this.$store.dispatch(
            "getPostcode",
            postcodeCoordinates
          );
          localStorage.setItem("postcode", postcode);
        }
      } catch (e) {
        console.error("An error occurred while geocoding postcode ", newValue);
      }

      // Log map zoom
      this.$store.dispatch("logSpeciesMapZoom");
    },

    electorateName(newValue) {
      // Set style to highlight electorate
      this.gMap.data.revertStyle();
      const electorate = this.allElectorates.find(
        (elct) => elct.h.CED_NAME21 === newValue
      );
      this.$store.commit("updateNumExtinct", electorate.h.NB_EXTINCT);
      this.gMap.data.overrideStyle(electorate, { fillOpacity: 0.9 });
      this.selectedElectorate = electorate;
    },
  },
  async mounted() {
    // Log landing
    this.$store.dispatch("logSpeciesPageLanding");
    this.gMap = null;

    // Load Map
    const loader = new google.maps.plugins.loader.Loader({
      apiKey: wwfconfigs.GOOGLE_MAP_KEY,
      version: "weekly",
      libraries: ["places"],
    });

    const mapOptions = {
      center: { lat: -25.99755, lng: 133.398094 },
      zoom: 5,
    };

    const electorateData = await this.$store.dispatch(
      "getElectoratesGrade",
      []
    );

    this.userPostcode = localStorage.getItem("postcode");
    this.userPostcodeLat = localStorage.getItem("postcodeLng");
    this.userPostcodeLng = localStorage.getItem("postcode");

    loader.loadCallback(async (error) => {
      if (error) {
        console.log(error);
      } else {
        this.gMap = new google.maps.Map(
          document.getElementById("map"),
          mapOptions
        );
        this.gMap.setOptions({ minZoom: 5 });
        const that = this;

        // Import GeoJson and add it to map
        this.gMap.data.addGeoJson(electorates);

        // Style GeoJson
        this.gMap.data.setStyle(function (feature) {
          const electorate = feature.getProperty("CED_NAME21");
          that.allElectorates.push(feature);
          const data = electorateData.find(
            (elec) => elec.electoraldivisionname === electorate
          );

          let color = "#757582";

          if (data) {
            feature.setProperty("GRADE", data.overallgrade);
            feature.setProperty(
              "NB_THREATENED_SPECIES",
              data.numberofthreatenedspeciesinelectorate
            );
            feature.setProperty(
              "NB_EXTINCT",
              data.numberofspeciesextinctlast10years
            );

            switch (data.overallgrade) {
              case "A":
                color = "#319362";
                break;
              case "B":
                color = "#87D178";
                break;
              case "C":
                color = "#EFBB3A";
                break;
              case "D":
                color = "#EA6647";
                break;
              case "E":
                color = "#AE3B49";
                break;
              case "F":
                color = "#733B3F";
                break;
            }
          }

          return {
            fillColor: color,
            fillOpacity: 0.6,
            strokeWeight: 1,
          };
        });

        // Add style on zone hover
        this.gMap.data.addListener("mouseover", (event) => {
          if (this.gMap) {
            this.gMap.data.revertStyle();

            if (this.selectedElectorate) {
              this.gMap.data.overrideStyle(this.selectedElectorate, {
                fillOpacity: 0.9,
              });
            }

            this.gMap.data.overrideStyle(event.feature, { fillOpacity: 0.8 });
          }
        });

        // Add style on zone hover
        this.gMap.data.addListener("mouseout", (event) => {
          if (this.gMap) {
            this.gMap.data.revertStyle();

            if (this.selectedElectorate) {
              this.gMap.data.overrideStyle(this.selectedElectorate, {
                fillOpacity: 0.9,
              });
            }
          }
        });

        // Add click listener
        this.gMap.data.addListener("click", function (event) {
          that.hasSelectedElectorate = true;
          that.selectElectorate(
            event.feature.getProperty("CED_NAME21"),
            event.feature.getProperty("GRADE"),
            event.feature.getProperty("NB_EXTINCT"),
            event.feature.getProperty("NB_THREATENED_SPECIES"),
            { lat: event.latLng.lat(), lng: event.latLng.lng() }
          );

          // Highlight clicked electorate;
          that.selectedElectorate = event.feature;
          that.gMap.data.overrideStyle(this.selectedElectorate, {
            fillOpacity: 0.9,
          });
        });

        // Get user's position
        const postcodeLat = parseFloat(localStorage.getItem("postcodeLat"));
        const postcodeLng = parseFloat(localStorage.getItem("postcodeLng"));

        if (postcodeLat && postcodeLng) {
          const userCoordinates = { lat: postcodeLat, lng: postcodeLng };
          const postcode = parseInt(localStorage.getItem("postcode"));

          if (!postcode) {
            const postcode = await this.$store.dispatch(
              "getPostcode",
              userCoordinates
            );
            localStorage.setItem("postcode", postcode);
          }

          const fromAU = localStorage.getItem("inAU");
          if (fromAU !== "outsideAU") {
            this.gMap.panTo(userCoordinates);
            this.gMap.setZoom(this.MAP_ZOOM);
          }
        } else {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const userCoordinates = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              };
              const postcode = await this.$store.dispatch(
                "getPostcode",
                userCoordinates
              );

              localStorage.setItem("postcode", postcode);
              const inAU = localStorage.getItem("inAU");

              if (inAU !== "outsideAU") {
                this.gMap.panTo(userCoordinates);
                this.gMap.setZoom(this.MAP_ZOOM);
              }

              localStorage.setItem("postcodeLat", position.coords.latitude);
              localStorage.setItem("postcodeLng", position.coords.longitude);
            },
            () => {
              localStorage.removeItem("inAU");
              this.$store.commit("openModal", "postcodeInfo");
            }
          );
        }
      }
    });
  },
  methods: {
    async selectElectorate(
      name,
      grade,
      numExtinct,
      nbThreatenedSpecies,
      coordinates
    ) {
      this.$store.commit("updateElectorateName", name);
      this.$store.commit("updateElectorateGrade", grade);
      this.$store.commit("updateNumExtinct", numExtinct);

      const speciesData = await this.$store.dispatch("getHeroSpecies", name);
      speciesData.speciesCount = nbThreatenedSpecies;

      this.$store.commit("updateSpeciesData", speciesData);

      if (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        )
      ) {
        this.$store.commit("updateOpenDrawer", false);
      } else {
        this.$store.commit("updateOpenDrawer", true);
      }

      this.gMap.panTo(coordinates);
      this.gMap.setZoom(this.MAP_ZOOM);

      // Log selection
      this.$store.dispatch("logSimpleEvent", {
        eventName: "Species Map Click",
        eventProperties: {
          "map nav": name,
          "source page": window.location.href,
        },
      });

      // Log map zoom
      this.$store.dispatch("logSpeciesMapZoom");
    },
  },
};
</script>

<style scoped>
.header {
  width: 100%;
  height: 100%;
}

.google-maps {
  width: 100%;
  height: 100%;
}

.map {
  width: 100%;
  height: 90%;
  overflow: visible;
}

.drawer {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 12%;
  right: 0%;
  width: 45%;
  z-index: 100;
}

@media only screen and (max-width: 40em) {
  .map {
    display: block;
  }
}
</style>


// WEBPACK FOOTER //
// ThreatenedSpeciesMap.vue?2974ad86